export type AppTourRoute = "/home" | "/library" | "/ai-tools";

export const toAppTourRoute = (path: string): AppTourRoute | null => {
    if (path.startsWith("/home")) return "/home";
    if (path.startsWith("/library")) return "/library";
    if (path.startsWith("/ai-tools")) return "/ai-tools";
    return null;
};

/**
 * One step of the app tour.
 *
 * Spotlight steps point at a `[data-tour-target="<target>"]` element on their
 * route; modal steps (`kind: "modal"`) have no target. Texts are plain English
 * sentences — they double as i18n keys (natural-key convention).
 */
export type AppTourStep = {
    id: string;
    route: AppTourRoute;
    title: string;
    body: string;
    /** Modal steps render a centered dialog instead of the spotlight + sheet. */
    kind?: "modal";
    /** `data-tour-target` attribute value of the highlighted element. */
    target?: string;
    /**
     * How the step advances: "next" — Next button only; "both" — Next button
     * or a real tap on the highlighted element (used for nav-tab steps).
     */
    advanceOn?: "next" | "both";
    /** Route the highlighted nav tab leads to; tour follows the navigation. */
    navigatesTo?: AppTourRoute;
    /** Label of the tap hint shown instead of a plain Next (e.g. "Tap Library"). */
    tapHint?: string;
    /** Keep the spotlight inside the visible band above the sheet. */
    clampToBand?: boolean;
    /** Scroll a taller-than-screen target through the spotlight. */
    autoScroll?: boolean;
    /** Hop the spotlight across siblings marked `data-tour-cycle="<value>"`. */
    cycle?: string;
    /** Auto-switch library tracks while the step is shown (demo). */
    demoSwitchTrack?: boolean;
    /** Spotlight corner radius override, px. */
    radius?: number;
    /** Modal-only fields. */
    chip?: string;
    mark?: "done";
    confetti?: boolean;
    primary?: string;
    secondary?: string;
    /** Web only: the step's target block is not rendered in the mobile apps. */
    webOnly?: boolean;
    /** Exists only when the roadmap certificate blocks are on (50/50 flag). */
    requiresRoadmapCertificate?: boolean;
};

const ALL_STEPS: AppTourStep[] = [
    {
        id: "welcome",
        route: "/home",
        kind: "modal",
        chip: "About a minute",
        title: "Your plan is ready!",
        body: "Let me show you around the app — it takes about a minute.",
        primary: "Show me around",
        secondary: "Skip for now"
    },
    {
        id: "roadmap_focus",
        route: "/home",
        target: "roadmap-focus",
        advanceOn: "next",
        title: "Your AI profile",
        body: "Your answers and choices shape your personal roadmap."
    },
    {
        id: "roadmap_edit",
        route: "/home",
        target: "roadmap-edit",
        advanceOn: "next",
        title: "You can change your roadmap anytime",
        body: "Use Change to choose a different learning direction."
    },
    {
        id: "cert_course",
        route: "/home",
        target: "certificate-course",
        advanceOn: "next",
        radius: 16,
        cycle: "certificate-course",
        requiresRoadmapCertificate: true,
        title: "Course certificates",
        body: "You get one for every course you complete."
    },
    {
        id: "cert_program",
        route: "/home",
        target: "certificate-final",
        advanceOn: "next",
        radius: 16,
        requiresRoadmapCertificate: true,
        title: "Program certificate",
        body: "One final certificate after completing all courses in your roadmap."
    },
    {
        id: "personal_plan",
        route: "/home",
        target: "personal-plan",
        advanceOn: "next",
        clampToBand: true,
        autoScroll: true,
        title: "Your personal roadmap",
        body: "Courses are arranged in the recommended order. Tap any one to start."
    },
    {
        id: "packs",
        route: "/home",
        target: "packs-block",
        advanceOn: "next",
        clampToBand: true,
        autoScroll: true,
        // The packs block is rendered only outside the apps (see the !isApp
        // guards around <PromptsLibraryPacks/>).
        webOnly: true,
        title: "Explore add-ons",
        body: "Here you can find all available add-ons."
    },
    {
        id: "streak",
        route: "/home",
        target: "streak",
        advanceOn: "next",
        title: "Keep your streak going",
        body: "Complete at least one lesson each day to keep your streak alive."
    },
    {
        id: "nav_library",
        route: "/home",
        target: "nav-library",
        advanceOn: "both",
        navigatesTo: "/library",
        title: "Explore the Library",
        body: "Browse all courses beyond your personal roadmap.",
        tapHint: "Tap Library"
    },
    {
        id: "library_intro",
        route: "/library",
        target: "library-tracks",
        advanceOn: "next",
        title: "Learning paths",
        body: "Courses are organized into paths like Careers, Challenges, Images, Video, Agents, and Text."
    },
    {
        id: "library_courses",
        route: "/library",
        target: "library-courses",
        advanceOn: "next",
        clampToBand: true,
        autoScroll: true,
        demoSwitchTrack: true,
        title: "Find a course you like",
        body: "Choose any course that interests you and start learning right away."
    },
    {
        id: "nav_ai_tools",
        route: "/library",
        target: "nav-ai-tools",
        advanceOn: "both",
        navigatesTo: "/ai-tools",
        title: "Meet your AI tools",
        body: "Your assistant and the generators.",
        tapHint: "Tap AI tools"
    },
    {
        id: "ai_popular",
        route: "/ai-tools",
        target: "ai-popular",
        advanceOn: "next",
        title: "Popular AI tools",
        body: "Quick access to some of the most popular tools."
    },
    {
        id: "ai_generate",
        route: "/ai-tools",
        target: "ai-generate",
        advanceOn: "next",
        title: "Text, images, video, audio",
        body: "Pick what to create — the model is already set up."
    },
    {
        id: "ai_quickstart",
        route: "/ai-tools",
        target: "ai-quickstart",
        advanceOn: "next",
        clampToBand: true,
        title: "Start with a ready-made prompt",
        body: "Choose a prompt for your task, then edit it to fit your needs."
    },
    {
        id: "ai_models",
        route: "/ai-tools",
        target: "ai-models",
        advanceOn: "next",
        clampToBand: true,
        autoScroll: true,
        title: "Choose your AI model",
        body: "Pick the model that fits your task — GPT-5, Gemini, Whisper, Wan, and more."
    },
    {
        id: "ai_tips",
        route: "/ai-tools",
        target: "ai-tips",
        advanceOn: "next",
        clampToBand: true,
        autoScroll: true,
        title: "Ready-made AI tips",
        body: "Short guides with prompts you can copy and use right away."
    },
    {
        id: "nav_home",
        route: "/ai-tools",
        target: "nav-home",
        advanceOn: "both",
        navigatesTo: "/home",
        title: "Back to your plan",
        body: "Return to Home and continue with your personal roadmap.",
        tapHint: "Tap Home"
    },
    {
        id: "finish",
        route: "/home",
        kind: "modal",
        mark: "done",
        confetti: true,
        title: "That's the tour!",
        body: "You know your way around now. Your roadmap is waiting on the Home screen — start whenever you're ready.",
        primary: "Got it"
    }
];

// The launch context persists the platform under this key before the tour can
// ever start (see PlatformContextModule), so a synchronous read is reliable.
const isAppPlatform = (): boolean => {
    try {
        const platform = localStorage.getItem("tixu_app_platform");
        return platform === "ios" || platform === "android";
    } catch {
        return false;
    }
};

const APP_STEPS = ALL_STEPS.filter((step) => !step.webOnly);

// Flag-gated blocks the steps point at. The tour model keeps this in sync with
// the resolved flags while the tour is idle — the roster must never change
// mid-run, or saved step indices would point at different steps.
let roadmapCertificateOn = false;

export const setAppTourRoadmapCertificate = (enabled: boolean): void => {
    roadmapCertificateOn = enabled;
};

/**
 * The tour steps for the current platform and flag arms. Web-only steps are
 * dropped in the mobile apps, certificate steps are dropped for the arm whose
 * home screen has no certificate blocks — so step indices, saved progress and
 * the "N of M" crumb all agree on one list.
 */
export const getAppTourSteps = (): AppTourStep[] =>
    (isAppPlatform() ? APP_STEPS : ALL_STEPS).filter(
        (step) => !step.requiresRoadmapCertificate || roadmapCertificateOn
    );
